import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EditPanel, { EditPanelProps } from '@/components/ui/Panels/EditPanel';
import TextInput from '@/components/ui/Form/TextInput';
import Checkbox from '@/components/ui/Form/Checkbox';
import {
  ExpirationFormat,
  getCardNameFromCyberSourceCardType,
  ICreditCardType,
  TCreditCardFormData
} from '@/components/ui/CreditCardGrid/CreditCard/helpers';
import { invalidDateError, minMonthError } from '@/utils/validations';
import { parseDate } from '@/utils/helpers';
import { isBeforeMinDate } from '@/components/ui/DatePicker/CalendarRange';
import { startOfMonth } from 'date-fns';
import useNudgeContext from '@/hooks/shared/useNudge/useNudgeContext';
import { ProfileNudgeSectionNames } from '@/hooks/shared/useNudge/utils';
import { useConvertTransientTokenMutation } from '@/redux/api/client/userPaymentMethods';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import Icon from '@/components/ui/Icon';
import useToast from '@/hooks/useToast';

declare global {
  interface Window {
    Flex: {
      new (captureContext: string): {
        microform: (options: { styles: Record<string, unknown> }) => {
          createField: (
            fieldType: string,
            options: { placeholder: string }
          ) => {
            on: (
              event: string,
              callback: (data: {
                empty: boolean;
                valid: boolean;
                card: { cybsCardType?: string }[];
              }) => void
            ) => void;
            load: (element: HTMLElement) => void;
          };
        };
      };
    };
  }
}

interface Microform {
  createField: (
    fieldType: string,
    options: { placeholder: string }
  ) => {
    on: (
      event: string,
      callback: (data: {
        empty: boolean;
        valid: boolean;
        card: { cybsCardType?: string }[];
      }) => void
    ) => void;
    load: (element: HTMLElement) => void;
  };
  createToken?: (
    options: { expirationMonth: string; expirationYear: string },
    callback: (err: { message: string } | null, token: string) => void
  ) => void;
}

interface ChangeNumberInputStatus {
  empty: boolean;
  valid: boolean;
  couldBeValid?: boolean;
  card?: { cybsCardType?: string }[];
}

const isValidExpirationDate = (expiration: string): boolean => {
  const expirationDate = parseDate(expiration, ExpirationFormat);
  return !!expirationDate && !isBeforeMinDate(expirationDate, startOfMonth(new Date()));
};

const expirationDateErrors = [
  invalidDateError(ExpirationFormat),
  minMonthError(new Date(), ExpirationFormat)
];

export type OnSaveCreditCard = (data: TCreditCardFormData) => Promise<void | (() => void)>;

type TransientTokenResponse = {
  data: {
    Id: string;
    CustomerTokenId: string;
    Status: string;
    Type: string;
  };
};

type CreditCardPanelProps = {
  onSave?: OnSaveCreditCard;
  handleLoadMicroformScript?: () => void;
  captureContextToken?: string;
  microformsScriptLoaded?: boolean;
} & Omit<EditPanelProps, 'children' | 'onSave' | 'onDelete' | 'title'>;

const MicroformsCreditCardPanel: React.FC<CreditCardPanelProps> = ({
  isOpen,
  onOpenChange,
  theme,
  onSave,
  handleLoadMicroformScript,
  captureContextToken,
  microformsScriptLoaded,
  triggerButton
}) => {
  const [name, setName] = useState<string>('');
  const [expiration, setExpiration] = useState<string>('');
  const [defaultCard, setDefaultCard] = useState<boolean>(false);
  const [cardType, setCardType] = useState<ICreditCardType | undefined>();
  const [isProcessing, setIsProcessing] = useState(false);
  const { openPanel, registerPanel, unregisterPanel } = useNudgeContext();
  const [showNumberError, setShowNumberError] = useState(false);
  const [showSecurityCodeError, setShowSecurityCodeError] = useState(false);
  const numberInputStatusRef = useRef<string>('empty');
  const securityCodeInputStatusRef = useRef<string>('empty');
  const decodedMaskedPANRef = useRef<string>('');
  const [microform, setMicroform] = useState<Microform | null>(null);
  const [startTimer, setStartTimer] = useState(false);

  const flexRef = useRef<InstanceType<Window['Flex']> | null>(null);
  const cardNumberRef = useRef<HTMLDivElement | null>(null);
  const securityCodeRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const screenSizeBelowLg = useMediaQuery(ScreenSizeQueries.belowLg);

  const [convertTransientTokenMutation] = useConvertTransientTokenMutation();

  const { showFailMessage, removeAllToasts } = useToast();

  useEffect(() => {
    setShowNumberError(numberInputStatusRef.current === 'invalid');
  }, [numberInputStatusRef]);

  useEffect(() => {
    setShowSecurityCodeError(securityCodeInputStatusRef.current === 'invalid');
  }, [securityCodeInputStatusRef]);

  const handleNumberInputBlur = useCallback(() => {
    if (
      numberInputStatusRef.current === 'empty' ||
      numberInputStatusRef.current === 'invalid' ||
      numberInputStatusRef.current === 'couldBeValid'
    ) {
      setShowNumberError(true);
    } else {
      setShowNumberError(false);
    }
  }, [numberInputStatusRef]);

  const handleSecurityCodeInputBlur = useCallback(() => {
    if (
      securityCodeInputStatusRef.current === 'empty' ||
      securityCodeInputStatusRef.current === 'invalid' ||
      securityCodeInputStatusRef.current === 'couldBeValid'
    ) {
      setShowSecurityCodeError(true);
    } else {
      setShowSecurityCodeError(false);
    }
  }, [securityCodeInputStatusRef]);

  const handleNumberInputChange = useCallback((data: ChangeNumberInputStatus) => {
    let numberStatus;
    switch (true) {
      case data.empty:
        numberStatus = 'empty';
        break;
      case !data.valid && data.couldBeValid:
        numberStatus = 'couldBeValid';
        break;
      case !data.valid && !data.couldBeValid:
        numberStatus = 'invalid';
        break;
      default:
        numberStatus = 'valid';
        break;
    }
    numberInputStatusRef.current = numberStatus;
    if (data.card?.[0]?.cybsCardType) {
      const cardType = getCardNameFromCyberSourceCardType(data.card[0].cybsCardType);
      setCardType(cardType ?? undefined);
    }
    setShowNumberError(false);
  }, []);

  const handleSecurityCodeInputChange = useCallback((data: ChangeNumberInputStatus) => {
    let securityCodeStatus;
    switch (true) {
      case data.empty:
        securityCodeStatus = 'empty';
        break;
      case !data.valid && data.couldBeValid:
        securityCodeStatus = 'couldBeValid';
        break;
      case !data.valid && !data.couldBeValid:
        securityCodeStatus = 'invalid';
        break;
      default:
        securityCodeStatus = 'valid';
        break;
    }
    securityCodeInputStatusRef.current = securityCodeStatus;
    setShowSecurityCodeError(false);
  }, []);

  const microformFieldStyles = useMemo(
    () => ({
      input: {
        'font-size': screenSizeBelowLg ? '14px' : '16px',
        'padding-top': '8px',
        'padding-bottom': '8px',
        'font-family': 'helvetica, tahoma, calibri, sans-serif',
        color: '#53565A',
        'max-height': '41px'
      },
      '::placeholder': {
        color: '#53565A'
      },
      ':disabled': { cursor: 'not-allowed' },
      valid: { 'border:': '1px solid #888888' }
    }),
    [screenSizeBelowLg]
  );

  const initializeMicroform = useCallback(() => {
    if (!captureContextToken) {
      console.error('Capture context token is missing.');
      return;
    }
    const flex = new window.Flex(captureContextToken);
    flexRef.current = flex;

    const microformInstance = flex.microform({ styles: microformFieldStyles });
    setMicroform(microformInstance);

    const numberField = microformInstance.createField('number', {
      placeholder: 'Card number *'
    });
    const securityCode = microformInstance.createField('securityCode', {
      placeholder: 'CVV *'
    });

    numberField.on('change', (data: ChangeNumberInputStatus) => {
      handleNumberInputChange(data);
    });

    numberField.on('blur', () => {
      handleNumberInputBlur();
    });

    securityCode.on('change', (data: ChangeNumberInputStatus) => {
      handleSecurityCodeInputChange(data);
    });

    securityCode.on('blur', () => {
      handleSecurityCodeInputBlur();
    });

    if (cardNumberRef.current) {
      numberField.load(cardNumberRef.current);
    }
    if (securityCodeRef.current) {
      securityCode.load(securityCodeRef.current);
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const expiration = getCaptureContextExpiration(captureContextToken);
    const msToExpire = expiration * 1000 - Date.now();
    const refreshIn = Math.max(0, msToExpire);

    timerRef.current = setTimeout(() => {
      setStartTimer(true);
    }, refreshIn);
  }, [
    captureContextToken,
    handleNumberInputBlur,
    handleNumberInputChange,
    handleSecurityCodeInputBlur,
    handleSecurityCodeInputChange,
    microformFieldStyles
  ]);

  const getCaptureContextExpiration = (jwt: string): number => {
    const [, payload] = jwt.split('.');
    const decoded = JSON.parse(atob(payload));
    return decoded.exp;
  };

  useEffect(() => {
    if (!isOpen) {
      setStartTimer(false);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    if (!captureContextToken) return;

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [captureContextToken, isOpen]);

  useEffect(() => {
    if (startTimer && timerRef.current) {
      const timedOutMessage =
        'Your session timed out for security reasons, and your payment details have been removed. Please re-enter to proceed.';
      handleLoadMicroformScript && handleLoadMicroformScript();
      showFailMessage(timedOutMessage, undefined, 'microform-timeout');
      timerRef.current = null;
    } else {
      setStartTimer(false);
    }
  }, [handleLoadMicroformScript, showFailMessage, startTimer]);

  useEffect(() => {
    if (isOpen && !microformsScriptLoaded) {
      handleLoadMicroformScript && handleLoadMicroformScript();
    }
  }, [handleLoadMicroformScript, isOpen, microformsScriptLoaded]);

  useEffect(() => {
    if (microformsScriptLoaded) {
      initializeMicroform();
    }
  }, [initializeMicroform, microformsScriptLoaded]);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setExpiration('');
      setShowNumberError(false);
      setShowSecurityCodeError(false);
      setDefaultCard(true);
      registerPanel(ProfileNudgeSectionNames.Payment);

      if (captureContextToken) {
        setTimeout(() => {
          handleLoadMicroformScript && handleLoadMicroformScript();
        }, 100);
      }
    } else {
      setMicroform(null);
      unregisterPanel(ProfileNudgeSectionNames.Payment);
    }
  }, [
    captureContextToken,
    initializeMicroform,
    isOpen,
    handleLoadMicroformScript,
    registerPanel,
    unregisterPanel
  ]);

  useEffect(() => {
    if (!isOpen && openPanel == ProfileNudgeSectionNames.Payment)
      unregisterPanel(ProfileNudgeSectionNames.Payment);
  }, [isOpen, openPanel, unregisterPanel]);

  const handleOnClose = useCallback(() => {
    removeAllToasts();
    onOpenChange?.(false);
  }, [onOpenChange, removeAllToasts]);

  const handleValidateFields = useCallback((): boolean => {
    const isNumberValid = numberInputStatusRef.current === 'valid';
    const isSecurityCodeValid = securityCodeInputStatusRef.current === 'valid';
    const isExpirationValid = isValidExpirationDate(expiration);

    if (!isNumberValid) {
      setShowNumberError(true);
    }
    if (!isSecurityCodeValid) {
      setShowSecurityCodeError(true);
    }

    return !!name && isNumberValid && isExpirationValid && isSecurityCodeValid;
  }, [expiration, name]);

  const decodeTransientToken = (tokenJwt: string) => {
    const payloadBase64 = tokenJwt.split('.')[1];
    const payloadJson = atob(payloadBase64);
    return JSON.parse(payloadJson);
  };

  const handleOnSave = useCallback(async () => {
    setIsProcessing(true);

    const [expirationMonth, expirationYear] = expiration.split('/');

    if (microform?.createToken) {
      const options = {
        expirationMonth: expirationMonth,
        expirationYear: expirationYear
      };

      microform.createToken(options, (err: { message: string } | null, token: string) => {
        if (err) {
          console.error('Tokenization error:', err);
        }
        const decodedPayload = token && decodeTransientToken(token);
        decodedMaskedPANRef.current =
          decodedMaskedPANRef.current = `${decodedPayload.content.paymentInformation.card.number.bin}${decodedPayload.content.paymentInformation.card.number.maskedValue.slice(decodedPayload.content.paymentInformation.card.number.bin.length)}`;

        const captureContext = {
          captureContext: captureContextToken,
          jwtToVerify: token
        };

        convertTransientTokenMutation(captureContext)
          .then(response => {
            if ('data' in response && response.data) {
              const tokenResponse = response as TransientTokenResponse;
              const data = {
                cardHolderName: name,
                cardNumber: decodedMaskedPANRef.current,
                expirationMonth,
                expirationYear,
                securityCode: '',
                isDefault: defaultCard,
                cyberSourceCardType: cardType?.cyberSourceCardType ?? '',
                cardTypeName: cardType?.type ?? '',
                token: tokenResponse.data.Id
              };
              onSave?.(data as TCreditCardFormData);
              handleOnClose();

              setIsProcessing(false);
            } else {
              console.error('Failed to retrieve tokenization response:', response);
            }
          })
          .catch(error => {
            console.error('Tokenization error:', error);
          });
      });
    } else {
      console.error('createToken method is not available on microform.');
    }
  }, [
    expiration,
    microform,
    captureContextToken,
    convertTransientTokenMutation,
    name,
    defaultCard,
    cardType?.cyberSourceCardType,
    cardType?.type,
    onSave,
    handleOnClose
  ]);

  const toggleDefaultCard = useCallback(() => setDefaultCard(state => !state), []);

  return (
    <EditPanel
      title="Add a new card"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      theme={theme}
      onValidateFields={handleValidateFields}
      onSave={handleOnSave}
      loading={isProcessing}
      triggerButton={triggerButton}
    >
      <TextInput
        label="Cardholder name"
        value={name}
        onChange={setName}
        acceptNumbers={false}
        acceptSpecialChars={false}
        required
      />
      {isOpen && (
        <div className="relative h-[41px] border-b border-b-gray-dark hover:border-b-black">
          <div className="flex h-[41px] items-end" ref={cardNumberRef}>
            <span className="text-1-medium py-2 text-gray-dark" id="tempNumPlaceholder">
              Card number *
            </span>
          </div>
          {showNumberError && (
            <div className="absolute">
              <span className="label mt-1 flex items-start gap-1 text-red">
                <Icon name="alert" size="small" />
                {numberInputStatusRef.current === 'empty'
                  ? 'Required'
                  : 'Please enter a valid card number'}
              </span>
            </div>
          )}
        </div>
      )}
      <TextInput
        label={`Expiration date (${ExpirationFormat.toUpperCase()})`}
        value={expiration}
        onChange={setExpiration}
        acceptLetters={false}
        acceptSpaces={false}
        acceptSpecialChars={false}
        allowedChars="/"
        maxLength={ExpirationFormat.length}
        errors={expirationDateErrors}
        required
      />
      {isOpen && (
        <div className="relative h-[41px] border-b border-b-gray-dark hover:border-b-black">
          <div className="flex h-[41px] items-end" ref={securityCodeRef}>
            <span className="text-1-medium py-2 text-gray-dark" id="tempSecPlaceholder">
              CVV *
            </span>
          </div>
          {showSecurityCodeError && (
            <div className="absolute">
              <span className="label mt-1 flex items-start gap-1 text-red">
                <Icon name="alert" size="small" />
                {securityCodeInputStatusRef.current === 'empty'
                  ? 'Required'
                  : 'Please enter a valid CVV'}
              </span>
            </div>
          )}
        </div>
      )}
      <Checkbox checked={defaultCard} onCheckedChange={toggleDefaultCard}>
        <Checkbox.Label>Make this card my default method of payment</Checkbox.Label>
      </Checkbox>
    </EditPanel>
  );
};

export default MicroformsCreditCardPanel;
